/**
 * ARAging — who owes what at 30/60/90 days. Data: GET /api/reports/ar-aging
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Wallet } from "lucide-react";

const money = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const BUCKETS = [
  ["current", "Current", "text-zinc-700"],
  ["b30", "1–30 Days", "text-amber-600"],
  ["b60", "31–60 Days", "text-amber-700"],
  ["b90", "61–90 Days", "text-red-600"],
  ["b90plus", "90+ Days", "text-red-700"],
];

export default function ARAging() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/reports/ar-aging").then((r) => setData(r.data)).catch(() => setData({ customers: [], totals: {} }));
  }, []);
  if (!data) return <div className="p-8 text-xs uppercase tracking-[0.2em] text-zinc-500">Loading…</div>;
  const t = data.totals || {};
  return (
    <div className="p-6 lg:p-8" data-testid="ar-aging-page">
      <div className="flex items-center gap-3 mb-2">
        <Wallet className="w-6 h-6 text-blue-700" />
        <h1 className="font-heading text-2xl font-black tracking-tight">A/R Aging</h1>
      </div>
      <p className="text-xs text-zinc-500 mb-5">Every unpaid invoice bucketed by days past due. Chase the right column first.</p>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
        {BUCKETS.map(([k, label, tone]) => (
          <div key={k} className="bg-white border border-zinc-200 rounded-sm p-3 text-center" data-testid={`ar-kpi-${k}`}>
            <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">{label}</div>
            <div className={`font-mono font-black text-sm ${tone}`}>{money(t[k])}</div>
          </div>
        ))}
        <div className="bg-zinc-950 text-white rounded-sm p-3 text-center" data-testid="ar-kpi-total">
          <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">Total Outstanding</div>
          <div className="font-mono font-black text-sm">{money(t.total)}</div>
        </div>
      </div>

      {(data.customers || []).length === 0 && (
        <div className="bg-white border border-dashed border-zinc-200 rounded-sm p-8 text-center text-sm text-zinc-500" data-testid="ar-empty">
          Nothing outstanding — every sent invoice is paid. 🎉
        </div>
      )}
      {(data.customers || []).map((c) => (
        <div key={c.customer} className="bg-white border border-zinc-200 rounded-sm mb-4" data-testid={`ar-customer-${c.customer}`}>
          <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-zinc-100 bg-zinc-50">
            <span className="font-black text-sm">{c.customer}</span>
            <span className="ml-auto font-mono text-sm font-black">{money(c.total)}</span>
          </div>
          <table className="w-full text-xs">
            <tbody>
              {c.invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-zinc-50 hover:bg-zinc-50">
                  <td className="px-4 py-2 font-mono font-bold"><Link to="/invoices" className="hover:text-blue-700">{inv.invoice_number}</Link></td>
                  <td className="px-3 py-2 text-zinc-500">{inv.project}</td>
                  <td className="px-3 py-2 font-mono">{inv.due_date ? `due ${inv.due_date}` : "no due date"}</td>
                  <td className="px-3 py-2">
                    {inv.days_past > 0
                      ? <span className={`font-bold ${inv.days_past > 60 ? "text-red-700" : "text-amber-700"}`}>{inv.days_past} days past due</span>
                      : <span className="text-zinc-400">current</span>}
                  </td>
                  <td className="px-4 py-2 text-right font-mono font-bold">{money(inv.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      <div className="text-[10px] text-zinc-400 mt-2">Send payment reminders from the Invoices page — reminders are always review-before-send.</div>
    </div>
  );
}
